import { useCallback } from "react";

import { useFetchWithRetry } from "../Utils/fetchWithRetry";
import { logError, logInfo } from "../Utils/loggers";

// Singleton in-memory cache to prevent redundant master data network requests
let portfolioDataCache = null;

// MAC platform/product/application lists change rarely and each lookup can take several seconds,
// so keep them for the session (with a TTL) and share requests that are already in flight.
const MAC_LIST_CACHE_TTL_MS = 30 * 60 * 1000;
const macListCache = new Map();
const copyList = (list) => (Array.isArray(list) ? [...list] : list);
const cachedMacList = (cacheKey, load) => {
  const hit = macListCache.get(cacheKey);
  if (hit && Date.now() - hit.at < MAC_LIST_CACHE_TTL_MS) return hit.promise.then(copyList);
  const promise = load();
  macListCache.set(cacheKey, { at: Date.now(), promise });
  promise.catch(() => {
    if (macListCache.get(cacheKey)?.promise === promise) macListCache.delete(cacheKey);
  });
  return promise.then(copyList);
};

export const MacDataServices = () => {
  const { fetchWithRetry } = useFetchWithRetry();

  const fetchPortfolioData = useCallback(
    async (apiEndpoint, forceRefresh = false) => {
      if (portfolioDataCache && !forceRefresh) {
        logInfo("fetchPortfolioData", "returning cached portfolio data");
        return portfolioDataCache;
      }
      logInfo("fetchPortfolioData", "fetching portfolio data from server");
      try {
        const url = apiEndpoint + "/getplatformportfoliodata";
        const data = await fetchWithRetry(url);
        logInfo("fetchPortfolioData", "fetched portfolio data successfully");
        portfolioDataCache = data.content;
        return data.content;
      } catch (error) {
        logError("fetchPortfolioData", "error fetching portfolio data", error);
        throw error;
      }
    },
    [fetchWithRetry]
  );

  const fetchPlatformData = useCallback(
    async (apiEndpoint, selectedPortfolio) =>
      cachedMacList(`platforms|${apiEndpoint}|${selectedPortfolio}`, async () => {
        logInfo("fetchPlatformData", "fetching platform data");
        try {
          const url = `${apiEndpoint}/portfolios/${selectedPortfolio}/platforms`;
          const data = await fetchWithRetry(url);
          logInfo("fetchPlatformData", "fetched platform data successfully");
          return data["platforms"];
        } catch (error) {
          logError("fetchPlatformData", "error fetching platform data", error);
          throw error;
        }
      }),
    [fetchWithRetry]
  );

  const fetchProductData = useCallback(
    async (apiEndpoint, selectedPlatform) => {
      logInfo("fetchProductData", "fetching product data");
      if (selectedPlatform) {
        return cachedMacList(`products|${apiEndpoint}|${selectedPlatform}`, async () => {
          try {
            const url = `${apiEndpoint}/platforms/${selectedPlatform}/products`;
            const data = await fetchWithRetry(url);
            logInfo("fetchProductData", "fetched product data successfully");
            return data["products"];
          } catch (error) {
            logError("fetchProductData", "error fetching product data", error);
            throw error;
          }
        });
      }
    },
    [fetchWithRetry]
  );

  const fetchApplicationData = useCallback(
    async (apiEndpoint, selectedProduct) => {
      logInfo("fetchApplicationData", "fetching application data");
      if (selectedProduct) {
        return cachedMacList(`applications|${apiEndpoint}|${selectedProduct}`, async () => {
          try {
            const url = `${apiEndpoint}/products/${selectedProduct}/applications`;
            const data = await fetchWithRetry(url);
            logInfo(
              "fetchApplicationData",
              "fetched application data successfully"
            );
            return data["applications"];
          } catch (error) {
            logError(
              "fetchApplicationData",
              "error fetching application data",
              error
            );
            throw error;
          }
        });
      }
    },
    [fetchWithRetry]
  );

  const fetchMacDataBasedOnApplicationId = useCallback(
    async (apiEndpoint, applicationID) => {
      if (applicationID) {
        logInfo("fetchMacDataBasedOnApplicationId", "fetching mac data");
        try { 
          let appID = applicationID.toUpperCase();
          const url = `${apiEndpoint}/macdata?appId=${appID}`;
          const data = await fetchWithRetry(url);
          logInfo(
            "fetchMacDataBasedOnApplicationId",
            "fetched mac data successfully"
          );
          return data;
        } catch (error) {
          logError(
            "fetchMacDataBasedOnApplicationId",
            "error fetching mac data",
            error
          );
          throw error;
        }
      }
    },

    [fetchWithRetry]
  );

  return {
    fetchPortfolioData,
    fetchPlatformData,
    fetchProductData,
    fetchApplicationData,
    fetchMacDataBasedOnApplicationId,
  };
};
